/**
 * Storyboard Continuity Audit — Continuity References Used header.
 *
 * Proves to the user which approved characters, environments, and
 * reference images informed the Storyboard agent's output.
 *
 * Read-only. Fetches the EXISTING `/api/projects/:id/production-continuity`
 * endpoint (same endpoint already used by ProductionContinuityPanel and
 * the Sprint 2 VisualBibleWorkspace). Renders thumbnails via the
 * EXISTING `/api/assets/:id/stream` endpoint.
 *
 * No new routes. No new AI calls. If the lock is empty or fails to
 * load, surfaces an amber warning so the storyboard's continuity gap
 * is visible to the user. Never silently passes.
 *
 * `useContinuityLookup()` is the shared resolver — `ShotMetadataChips`
 * inside ProjectBoard re-uses it via the `lookup` prop to map
 * `character_ids` / `environment_id` to NAMES rather than raw UUIDs.
 */
import { useEffect, useMemo, useState } from "react";
import { Users, Mountain, Image as ImageIcon, AlertTriangle, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import ContinuityThumb from "./ContinuityThumb";

/** Build a stable name-lookup index from the lock payload. */
export function buildContinuityLookup(lockResp) {
    const lock = lockResp?.lock || lockResp || {};
    const chars = (lock.character_bible?.items) || lock.characters || [];
    const envs = (lock.environment_bible?.items) || lock.environments || [];
    const charById = {};
    const envById = {};
    const allAssetIds = new Set();
    for (const c of chars) {
        if (!c?.id) continue;
        charById[c.id] = {
            id: c.id,
            name: c.name || c.id,
            approval: c.approval_status,
            refs: c.reference_asset_ids || [],
            gen: c.generated_image_asset_id || null,
        };
        for (const r of (c.reference_asset_ids || [])) allAssetIds.add(r);
        if (c.generated_image_asset_id) allAssetIds.add(c.generated_image_asset_id);
    }
    for (const e of envs) {
        if (!e?.id) continue;
        envById[e.id] = {
            id: e.id,
            name: e.name || e.id,
            approval: e.approval_status,
            refs: e.reference_asset_ids || [],
            gen: e.generated_image_asset_id || null,
        };
        for (const r of (e.reference_asset_ids || [])) allAssetIds.add(r);
        if (e.generated_image_asset_id) allAssetIds.add(e.generated_image_asset_id);
    }
    return {
        chars: Object.values(charById).filter((c) => c.approval === "approved"),
        envs: Object.values(envById).filter((e) => e.approval === "approved"),
        charById,
        envById,
        allAssetIds: Array.from(allAssetIds),
    };
}

/** Custom hook for loading + caching the continuity lock at the
 *  StoryboardPanel level. Exported so ShotMetadataChips can share it. */
export function useContinuityLookup(projectId) {
    const [raw, setRaw] = useState(null);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        let mounted = true;
        if (!projectId) return;
        (async () => {
            try {
                const { data } = await api.get(`/projects/${projectId}/production-continuity`);
                if (mounted) setRaw(data);
            } catch (e) {
                /* silent — header renders a graceful warning instead. */
            } finally {
                if (mounted) setLoaded(true);
            }
        })();
        return () => { mounted = false; };
    }, [projectId]);
    const lookup = useMemo(() => buildContinuityLookup(raw), [raw]);
    return { lookup, loaded, raw };
}

export default function ContinuityReferencesHeader({ project, lookup, loaded, onOpenAsset }) {
    if (!loaded) {
        return (
            <div
                data-testid="continuity-refs-loading"
                className="mx-6 lg:mx-10 mt-4 mb-2 border border-zinc-900 bg-zinc-950 rounded-sm px-5 py-4 text-xs font-mono uppercase tracking-wider text-zinc-500"
            >
                Loading continuity references…
            </div>
        );
    }
    const chars = lookup?.chars || [];
    const envs = lookup?.envs || [];
    const totalRefAssets = (lookup?.allAssetIds || []).length;
    const aiRefCount = [...chars, ...envs].filter((x) => !!x.gen).length;

    // Empty-state — surfaces an explicit warning so the user knows the
    // storyboard agent ran WITHOUT approved continuity references.
    if (chars.length === 0 && envs.length === 0) {
        return (
            <div
                data-testid="continuity-refs-header-empty"
                className="mx-6 lg:mx-10 mt-4 mb-2 border border-amber-500/40 bg-amber-500/5 rounded-sm px-5 py-4 flex items-start gap-3"
            >
                <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                <div>
                    <div className="label-micro text-amber-300 mb-1">No continuity references</div>
                    <p className="text-xs text-amber-100/80 max-w-2xl">
                        This storyboard was generated without any approved characters or environments in Visual Bible. Consider approving continuity references before re-running the storyboard agent for better consistency.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="continuity-refs-header"
            className="mx-6 lg:mx-10 mt-4 mb-3 border border-zinc-800 bg-gradient-to-br from-[#0c0a08] via-[#0a0a0a] to-black rounded-sm overflow-hidden"
        >
            <header className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                    <div className="label-micro text-emerald-300">Continuity References Used</div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    {chars.length} character{chars.length === 1 ? "" : "s"} · {envs.length} environment{envs.length === 1 ? "" : "s"} · {totalRefAssets} reference asset{totalRefAssets === 1 ? "" : "s"}{aiRefCount > 0 ? ` (${aiRefCount} AI-generated)` : ""}
                </div>
            </header>

            <div className="px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                {/* Characters */}
                <div>
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">
                        <Users className="w-3.5 h-3.5" />
                        Characters · {chars.length}
                    </div>
                    {chars.length === 0 ? (
                        <div className="text-xs text-zinc-600">No approved characters.</div>
                    ) : (
                        <ul className="space-y-2" data-testid="continuity-refs-characters">
                            {chars.map((c) => {
                                const thumbId = c.gen || c.refs?.[0];
                                return (
                                    <li
                                        key={c.id}
                                        data-testid={`continuity-char-${c.id.slice(0, 8)}`}
                                        className="flex items-center gap-3"
                                    >
                                        {thumbId ? <ContinuityThumb assetId={thumbId} size={36} title={c.name} onOpen={onOpenAsset} /> : <span className="w-9 h-9 inline-flex items-center justify-center border border-zinc-800 bg-zinc-950 text-zinc-700 rounded-sm shrink-0"><Users className="w-3.5 h-3.5" /></span>}
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate">{c.name}</div>
                                            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                                                approved · {c.refs.length} ref{c.refs.length === 1 ? "" : "s"}{c.gen ? " · 1 AI" : ""}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Environments */}
                <div>
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">
                        <Mountain className="w-3.5 h-3.5" />
                        Environments · {envs.length}
                    </div>
                    {envs.length === 0 ? (
                        <div className="text-xs text-zinc-600">No approved environments.</div>
                    ) : (
                        <ul className="space-y-2" data-testid="continuity-refs-environments">
                            {envs.map((e) => {
                                const thumbId = e.gen || e.refs?.[0];
                                return (
                                    <li
                                        key={e.id}
                                        data-testid={`continuity-env-${e.id.slice(0, 8)}`}
                                        className="flex items-center gap-3"
                                    >
                                        {thumbId ? <ContinuityThumb assetId={thumbId} size={36} title={e.name} onOpen={onOpenAsset} /> : <span className="w-9 h-9 inline-flex items-center justify-center border border-zinc-800 bg-zinc-950 text-zinc-700 rounded-sm shrink-0"><Mountain className="w-3.5 h-3.5" /></span>}
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate">{e.name}</div>
                                            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                                                approved · {e.refs.length} ref{e.refs.length === 1 ? "" : "s"}{e.gen ? " · 1 AI" : ""}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            <footer className="px-5 py-2 border-t border-zinc-900 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                <ImageIcon className="w-3 h-3" />
                Reference images flow into every storyboard shot via the production_continuity prompt block.
            </footer>
        </div>
    );
}
